import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import { 
  Users, 
  DollarSign, 
  Search,
  AlertTriangle,
  Mail,
  Phone,
  Globe,
  CheckCircle,
  UserPlus,
  FileText,
  MessageCircle,
  Clock
} from 'lucide-react';

/**
 * Composant pour les prospects assignés à l'employé/manager
 * Permet de gérer et d'affecter les prospects au conseiller en immigration
 */
export default function MyProspects() {
  const [prospects, setProspects] = useState([]);
  const [filteredProspects, setFilteredProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [transactionRef, setTransactionRef] = useState('');
  const [consultationType, setConsultationType] = useState('personne'); // 'personne' ou 'famille'
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [conversionData, setConversionData] = useState({
    country: '',
    visa_type: '',
    first_payment_amount: 0
  });
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailProspect, setDetailProspect] = useState(null);

  const statusLabels = {
    'assigne_employe': { label: 'À Contacter', color: 'bg-purple-500', icon: '📞' },
    'paiement_50k': { label: 'Chez le conseiller en immigration', color: 'bg-green-500', icon: '💼' },
    'en_consultation': { label: 'En Consultation', color: 'bg-orange-500', icon: '💬' },
    'converti_client': { label: 'Devenu Client', color: 'bg-emerald-500', icon: '✅' }
  };

  useEffect(() => {
    fetchMyProspects();

    // Supprimé: Auto-refresh automatique qui causait des pertes de données
    // Les données seront mises à jour uniquement après les actions utilisateur

    return () => {};
  }, []);

  useEffect(() => {
    filterProspects();
  }, [prospects, searchTerm]);

  const fetchMyProspects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact-messages');
      setProspects(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des prospects:', error);
      toast.error('Impossible de charger vos prospects');
    } finally {
      setLoading(false);
    }
  };

  const filterProspects = () => {
    let filtered = [...prospects];

    if (searchTerm) {
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.country?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredProspects(filtered);
  };

  const handleAffectToConsultant = async () => {
    if (!selectedProspect) return;

    try {
      const response = await api.patch(`/contact-messages/${selectedProspect.id}/assign-consultant`, {
        payment_method: paymentMethod,
        transaction_reference: transactionRef || null,
        consultation_type: consultationType
      });
      
      const amount = consultationType === 'famille' ? 100000 : 50000;
      toast.success(`Paiement de ${amount.toLocaleString()} CFA enregistré ! Facture: ${response.data.invoice_number}`);
      setShowConfirmDialog(false);
      setSelectedProspect(null);
      setPaymentMethod('Cash');
      setTransactionRef('');
      setConsultationType('personne');
      fetchMyProspects();
    } catch (error) {
      console.error('Erreur lors de l\'affectation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'affectation au conseiller en immigration');
    }
  };

  const handleConvertToClient = async (prospect) => {
    setSelectedProspect(prospect);
    setConversionData({
      country: prospect.country || '',
      visa_type: prospect.visa_type || '',
      first_payment_amount: 0
    });
    setShowConversionDialog(true);
  };

  const confirmConversion = async () => {
    if (!conversionData.country || !conversionData.visa_type) {
      toast.error('Veuillez renseigner le pays et le type de visa');
      return;
    }

    try {
      const response = await api.post(`/contact-messages/${selectedProspect.id}/convert-to-client`, conversionData);
      
      // Afficher confirmation sans exposer le mot de passe
      toast.success(
        <div>
          <p className="font-semibold">✅ Client créé avec succès!</p>
          <div className="mt-3 bg-slate-800 p-3 rounded">
            <p className="text-sm">📧 Email: <span className="font-mono">{response.data.login_email}</span></p>
            <p className="text-sm mt-2 text-green-400">🔐 Identifiants envoyés par email</p>
            <p className="text-xs mt-1 text-slate-400">Le client a reçu ses identifiants de connexion par email</p>
          </div>
        </div>,
        { duration: 10000 }
      );
      
      setShowConversionDialog(false);
      fetchMyProspects();
    } catch (error) {
      console.error('Erreur conversion:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion en client');
    }
  };

  const openConfirmDialog = (prospect) => {
    setSelectedProspect(prospect);
    setShowConfirmDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const prospectsToContact = prospects.filter(p => p.status === 'assigne_employe');
  const prospectsWithConsultant = prospects.filter(p => ['paiement_50k', 'en_consultation'].includes(p.status));
  const convertedClients = prospects.filter(p => p.status === 'converti_client');

  return (
    <div className="space-y-6">
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">À Contacter</p>
                <p className="text-2xl font-bold text-white">{prospectsToContact.length}</p>
              </div>
              <div className="text-3xl">📞</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/10 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Chez le conseiller en immigration</p>
                <p className="text-2xl font-bold text-white">{prospectsWithConsultant.length}</p>
              </div>
              <div className="text-3xl">💼</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Convertis</p>
                <p className="text-2xl font-bold text-white">{convertedClients.length}</p>
              </div>
              <div className="text-3xl">✅</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recherche */}
      <Card className="bg-[#1E293B] border-slate-700">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-slate-400" />
            <Input
              placeholder="Rechercher un prospect..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Liste des prospects */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <Users className="w-6 h-6 text-orange-500" />
          Mes Prospects ({filteredProspects.length})
        </h3>

        {filteredProspects.length === 0 ? (
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-12 text-center">
              <p className="text-slate-400">Aucun prospect assigné</p>
            </CardContent>
          </Card>
        ) : (
          filteredProspects.map((prospect) => (
            <Card 
              key={prospect.id} 
              className="bg-[#1E293B] border-slate-700 hover:border-orange-500/50 transition-all"
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Informations */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h4 className="text-lg font-bold text-white">{prospect.name}</h4>
                      <Badge className={`${statusLabels[prospect.status]?.color || 'bg-gray-500'} text-white`}>
                        {statusLabels[prospect.status]?.icon} {statusLabels[prospect.status]?.label}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Mail className="w-4 h-4 text-slate-400" />
                        {prospect.email}
                      </div>
                      {prospect.phone && (
                        <div className="flex items-center gap-2 text-slate-300">
                          <Phone className="w-4 h-4 text-slate-400" />
                          {prospect.phone}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-slate-300">
                        <Globe className="w-4 h-4 text-slate-400" />
                        {prospect.country} - {prospect.visa_type}
                      </div>
                    </div>

                    {prospect.message && (
                      <p className="text-slate-400 text-sm italic">
                        "{prospect.message.substring(0, 150)}..."
                      </p>
                    )}

                    {prospect.payment_50k_amount && (
                      <Badge className="bg-green-500/20 text-green-400">
                        💰 {prospect.payment_50k_amount} CFA payés
                      </Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 min-w-[200px]">
                    {prospect.status === 'assigne_employe' && (
                      <>
                        <Button 
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600"
                          onClick={() => openConfirmDialog(prospect)}
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Affecter au conseiller en immigration
                        </Button>
                        <p className="text-xs text-slate-400 text-center mt-1">
                          💡 Paiement requis: 50 000 CFA pour consultation
                        </p>
                        <p className="text-xs text-orange-400 text-center">
                          ⚠️ Conversion client possible APRÈS consultation
                        </p>
                      </>
                    )}

                    {prospect.status === 'paiement_50k' && (
                      <>
                        <Badge className="bg-green-500/20 text-green-400 justify-center py-2 mb-2">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Chez le conseiller en immigration
                        </Badge>
                        <Button 
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600"
                          onClick={() => handleConvertToClient(prospect)}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Convertir en Client
                        </Button>
                      </>
                    )}

                    {prospect.status === 'en_consultation' && (
                      <>
                        <Badge className="bg-orange-500/20 text-orange-400 justify-center py-2 mb-2">
                          💬 En consultation
                        </Badge>
                        <Button 
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600"
                          onClick={() => handleConvertToClient(prospect)}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Convertir en Client
                        </Button>
                      </>
                    )}

                    {prospect.status === 'converti_client' && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 justify-center py-2">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Client créé
                      </Badge>
                    )}

                    {/* Bouton Détails - disponible pour tous les prospects */}
                    <Button 
                      size="sm"
                      variant="ghost"
                      className="text-slate-400 hover:text-white mt-2"
                      onClick={() => {
                        setDetailProspect(prospect);
                        setShowDetailsDialog(true);
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Détails
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Dialog de confirmation */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Confirmation Paiement & Affectation
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Veuillez confirmer que le prospect a bien effectué le paiement avant de l'affecter au conseiller en immigration.
            </DialogDescription>
          </DialogHeader>
          
          {selectedProspect && (
            <div className="space-y-4 mt-4">
              <div className="bg-slate-800 p-4 rounded-lg space-y-2">
                <p className="text-white font-semibold">{selectedProspect.name}</p>
                <p className="text-slate-400 text-sm">{selectedProspect.email}</p>
                <p className="text-slate-400 text-sm">{selectedProspect.country} - {selectedProspect.visa_type}</p>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/30 p-4 rounded-lg">
                <p className="text-orange-400 font-semibold mb-2 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Montant requis: {consultationType === 'famille' ? '100 000 CFA' : '50 000 CFA'}
                </p>
                <p className="text-slate-300 text-sm">
                  Ce montant couvre la consultation approfondie avec le conseiller en immigration.
                  Un email de confirmation sera envoyé automatiquement au prospect.
                </p>
              </div>

              <div className="space-y-3">
                <div>
                  <Label className="text-slate-300">Type de consultation *</Label>
                  <div className="mt-2 flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="personne"
                        checked={consultationType === 'personne'}
                        onChange={() => setConsultationType('personne')}
                        className="accent-orange-500"
                      />
                      Personne (50 000 CFA)
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        value="famille"
                        checked={consultationType === 'famille'}
                        onChange={() => setConsultationType('famille')}
                        className="accent-orange-500"
                      />
                      Famille (100 000 CFA)
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-slate-300">Méthode de paiement *</Label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full mt-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="Cash">Cash (Espèces)</option>
                    <option value="Mobile Money">Mobile Money</option>
                    <option value="Virement">Virement Bancaire</option>
                  </select>
                </div>

                <div>
                  <Label className="text-slate-300">Référence transaction (optionnelle)</Label>
                  <Input
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                    placeholder="Ex: MTN-123456789 ou N° de reçu"
                    className="mt-2 bg-slate-800 border-slate-600 text-white"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1 border-slate-600 text-white hover:bg-slate-700"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setSelectedProspect(null);
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  onClick={handleAffectToConsultant}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmer Affectation
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de conversion en client */}
      <Dialog open={showConversionDialog} onOpenChange={setShowConversionDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-blue-500" />
              Convertir en Client
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Veuillez compléter les informations pour créer le compte client et son dossier d'immigration.
            </DialogDescription>
          </DialogHeader>
          
          {selectedProspect && (
            <div className="space-y-6 mt-4">
              <div className="bg-slate-800 p-4 rounded-lg space-y-2">
                <p className="text-white font-semibold">{selectedProspect.name}</p>
                <p className="text-slate-400 text-sm">{selectedProspect.email}</p>
                <p className="text-slate-400 text-sm">{selectedProspect.phone}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Pays de destination *</Label>
                  <select
                    value={conversionData.country}
                    onChange={(e) => setConversionData({...conversionData, country: e.target.value, visa_type: ''})}
                    className="w-full mt-1 bg-slate-800 border-slate-600 text-white p-2 rounded"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Canada">Canada</option>
                    <option value="France">France</option>
                    <option value="Belgique">Belgique</option>
                  </select>
                </div>

                <div>
                  <Label className="text-slate-300">Type de visa *</Label>
                  <select
                    value={conversionData.visa_type}
                    onChange={(e) => setConversionData({...conversionData, visa_type: e.target.value})}
                    className="w-full mt-1 bg-slate-800 border-slate-600 text-white p-2 rounded"
                    disabled={!conversionData.country}
                  >
                    <option value="">Sélectionner...</option>
                    {conversionData.country === 'Canada' && (
                      <>
                        <option value="Permis de travail">Permis de travail</option>
                        <option value="Permis d'études">Permis d'études</option>
                        <option value="Résidence permanente (EE/Arrima)">Résidence permanente (EE/Arrima)</option>
                      </>
                    )}
                    {conversionData.country === 'France' && (
                      <>
                        <option value="Permis de travail (Passeport Talent)">Permis de travail (Passeport Talent)</option>
                        <option value="Visa étudiant">Visa étudiant</option>
                        <option value="Regroupement familial">Regroupement familial</option>
                      </>
                    )}
                    {conversionData.country === 'Belgique' && (
                      <>
                        <option value="Permis Unique / Travail Salarié (incluant Carte Bleue Européenne)">Permis Unique / Travail Salarié (incluant Carte Bleue Européenne)</option>
                        <option value="Visa D Étudiant">Visa D Étudiant</option>
                        <option value="Regroupement Familial">Regroupement Familial</option>
                        <option value="Carte Professionnelle (Travailleur Indépendant)">Carte Professionnelle (Travailleur Indépendant)</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Premier versement (CFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={conversionData.first_payment_amount}
                  onChange={(e) => setConversionData({...conversionData, first_payment_amount: parseFloat(e.target.value) || 0})}
                  placeholder="Montant du premier versement"
                  className="mt-1 bg-slate-800 border-slate-600 text-white"
                />
                <p className="text-slate-500 text-xs mt-1">Optionnel - Si le client a déjà effectué un paiement</p>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                <p className="text-blue-400 text-sm">
                  ℹ️ Un email sera automatiquement envoyé au client avec ses identifiants de connexion sécurisés.
                </p>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  className="flex-1 border-slate-600 text-white hover:bg-slate-700"
                  onClick={() => {
                    setShowConversionDialog(false);
                    setSelectedProspect(null);
                  }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                  onClick={confirmConversion}
                  disabled={!conversionData.country || !conversionData.visa_type}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Créer le Client
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Détails du Prospect */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Détails du Prospect
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Informations complètes fournies par le prospect
            </DialogDescription>
          </DialogHeader>
          
          {detailProspect && (
            <div className="space-y-6 mt-4">
              {/* Informations personnelles */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-orange-500" />
                    Informations Personnelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm">Nom Complet</Label>
                      <p className="text-white font-medium">{detailProspect.name}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Statut</Label>
                      <div>
                        <Badge className={`${statusLabels[detailProspect.status]?.color || 'bg-gray-500'} text-white`}>
                          {statusLabels[detailProspect.status]?.icon} {statusLabels[detailProspect.status]?.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </Label>
                      <p className="text-white">{detailProspect.email}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        Téléphone
                      </Label>
                      <p className="text-white">{detailProspect.phone || 'Non renseigné'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-400 text-sm flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        Pays d'origine
                      </Label>
                      <p className="text-white">{detailProspect.country}</p>
                    </div>
                    <div>
                      <Label className="text-slate-400 text-sm">Type de visa souhaité</Label>
                      <p className="text-white">{detailProspect.visa_type || 'Non renseigné'}</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-slate-400 text-sm">Comment nous a-t-il connu ?</Label>
                    <p className="text-white">{detailProspect.how_did_you_know || 'Non renseigné'}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Message du prospect */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-500" />
                    Message du Prospect
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-slate-900 p-4 rounded-lg">
                    <p className="text-white whitespace-pre-wrap">{detailProspect.message}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Informations d'assignation */}
              {detailProspect.assigned_to_name && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-purple-500" />
                      Assignation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2">
                      <Label className="text-slate-400 text-sm">Assigné à:</Label>
                      <p className="text-white font-medium">{detailProspect.assigned_to_name}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Paiement consultation */}
              {detailProspect.payment_50k_amount && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      Paiement Consultation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-slate-400 text-sm">Montant:</Label>
                      <p className="text-white font-medium">{detailProspect.payment_50k_amount} CFA</p>
                    </div>
                    {detailProspect.payment_50k_date && (
                      <div className="flex items-center gap-2">
                        <Label className="text-slate-400 text-sm">Date:</Label>
                        <p className="text-white">{new Date(detailProspect.payment_50k_date).toLocaleDateString('fr-FR')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Notes du conseiller */}
              {detailProspect.consultant_notes && detailProspect.consultant_notes.length > 0 && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-orange-500" />
                      Notes du Conseiller en Immigration
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {detailProspect.consultant_notes.map((note, idx) => (
                        <div key={idx} className="bg-slate-900 p-4 rounded-lg border-l-4 border-orange-500">
                          <p className="text-white mb-2">{note.content}</p>
                          <div className="flex items-center gap-4 text-xs text-slate-400">
                            <span>Par: {note.created_by}</span>
                            <span>Le: {new Date(note.created_at).toLocaleDateString('fr-FR', { 
                              day: '2-digit', 
                              month: '2-digit', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</span>
                          </div>
                          {note.is_potential_client && (
                            <Badge className="mt-2 bg-emerald-500/20 text-emerald-400">
                              🎯 Potentiel client: {note.potential_level}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dates */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-500" />
                    Historique
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-sm">Date de création:</Label>
                    <p className="text-white">{new Date(detailProspect.created_at).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-slate-400 text-sm">Dernière mise à jour:</Label>
                    <p className="text-white">{new Date(detailProspect.updated_at).toLocaleDateString('fr-FR', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
